<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import type { Snapshot } from '@/services/apiClient'
import { apiClient } from '@/services/apiClient'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  restore: [snapshotId: number]
  close: []
  'clear-all-snapshots': []
}>()

const snapshots = ref<Snapshot[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const selectedId = ref<number | null>(null)

// 加載快照列表
const loadSnapshots = async () => {
  loading.value = true
  error.value = null
  try {
    snapshots.value = await apiClient.getSnapshots()
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加載失敗'
  } finally {
    loading.value = false
  }
}

// 恢復快照
const restoreSelected = async () => {
  if (!selectedId.value) return

  loading.value = true
  try {
    await apiClient.restoreSnapshot(selectedId.value)
    emit('restore', selectedId.value)
  } catch (err) {
    error.value = err instanceof Error ? err.message : '恢復失敗'
  } finally {
    loading.value = false
  }
}

// 刪除快照
const deleteSnapshot = async (id: number) => {
  if (!confirm('確定要刪除此版面配置嗎？')) return

  try {
    await apiClient.deleteSnapshot(id)
    snapshots.value = snapshots.value.filter(s => s.id !== id)
    if (selectedId.value === id) {
      selectedId.value = null
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : '刪除失敗'
  }
}

// 計算快照統計
const snapshotCount = computed(() => snapshots.value.length)
const isFull = computed(() => snapshotCount.value >= 20)

// 格式化時間為台灣時區
const formatTimestamp = (isoString: string): string => {
  const date = new Date(isoString)
  return new Intl.DateTimeFormat('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date)
}

// 清除所有快照
const handleClearAllSnapshots = () => {
  if (!confirm('確定要刪除所有版面配置歷史嗎？此操作無法撤銷。')) {
    return
  }
  emit('clear-all-snapshots')
}

onMounted(() => {
  if (props.isOpen) {
    loadSnapshots()
  }
})

// 監聽 isOpen 變化
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      loadSnapshots()
    }
  }
)
</script>

<template>
  <div
    class="snapshot-panel"
    :class="{ 'is-open': isOpen }"
    @click="emit('close')"
  >
    <div class="backdrop"></div>

    <div class="panel" @click.stop>
      <div class="panel-header">
        <h3>版面記錄</h3>
        <button class="close-btn" @click="emit('close')" title="關閉">
          ✕
        </button>
      </div>

      <div class="panel-toolbar">
        <button
          class="panel-toolbar-btn"
          @click="restoreSelected"
          :disabled="!selectedId || loading"
          title="恢復選中的版面配置"
        >
          ⏮️ 恢復版面
        </button>
        <button
          class="panel-toolbar-btn danger"
          @click="handleClearAllSnapshots"
          title="刪除所有版面配置歷史"
        >
          🗑️ 清除記錄
        </button>
      </div>

      <div class="panel-stats">
        <span>已儲存 {{ snapshotCount }} / 20 個版面</span>
        <span v-if="isFull" class="full-warning">⚠️ 已達上限</span>
      </div>

      <div class="snapshots-list">
        <div v-if="loading && snapshots.length === 0" class="loading">載入中...</div>

        <div v-else-if="snapshots.length === 0" class="empty">
          還沒有任何版面配置
        </div>

        <div v-else-if="error" class="error">
          ⚠️ {{ error }}
        </div>

        <div v-else class="items">
          <div
            v-for="snapshot in snapshots"
            :key="snapshot.id"
            class="snapshot-item"
            :class="{ selected: selectedId === snapshot.id }"
            @click="selectedId = snapshot.id"
          >
            <div class="item-content">
              <div class="item-header">
                <span class="time">⏰ {{ snapshot.relative_time }}</span>
                <span class="count">({{ snapshot.snapshot_name.match(/\d+/)?.[0] }} frames)</span>
              </div>
              <div class="item-timestamp">
                {{ formatTimestamp(snapshot.created_at) }}
              </div>
            </div>
            <button
              class="item-delete-btn"
              @click.stop="deleteSnapshot(snapshot.id)"
              title="刪除此版面配置"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.snapshot-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
  pointer-events: none;
}

.snapshot-panel.is-open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
}

.panel {
  position: absolute;
  top: 31px;
  right: 0;
  bottom: 0;
  width: 320px;
  max-width: 100%;
  background-color: white;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f5f5f5;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.panel-toolbar {
  display: flex;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
  justify-content: center;
}

.panel-toolbar-btn {
  background-color: transparent;
  border: 1px solid #999;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
  color: #333;
}

.panel-toolbar-btn:hover:not(:disabled) {
  background-color: #d0d0d0;
  border-color: #666;
}

.panel-toolbar-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.panel-toolbar-btn.danger {
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.panel-toolbar-btn.danger:hover:not(:disabled) {
  background-color: #fff5f5;
  border-color: #ff4444;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.panel-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  font-size: 13px;
  color: #666;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.full-warning {
  color: #ff6b6b;
  font-weight: 500;
}

.snapshots-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.loading,
.error,
.empty {
  padding: 32px 16px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.error {
  color: #ff6b6b;
  padding: 16px;
  background-color: #fff5f5;
}

.items {
  padding: 0;
}

.snapshot-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  user-select: none;
}

.snapshot-item:hover {
  background-color: #f9f9f9;
}

.snapshot-item.selected {
  background-color: #e3f2fd;
  border-left: 3px solid #2196f3;
  padding-left: 9px;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.time {
  flex: 1;
}

.count {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.item-timestamp {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.item-delete-btn {
  flex-shrink: 0;
  background: none;
  border: 1px solid #ddd;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-delete-btn:hover {
  background-color: #fff5f5;
  border-color: #ff6b6b;
  color: #ff6b6b;
}

@media (max-width: 600px) {
  .panel {
    width: 100%;
  }
}
</style>
